import { useEffect, type MouseEvent, type ReactNode, type Ref } from "react"
import { createPortal } from "react-dom"
import { useEditorPortalRoot } from "./EditorPortalContext"

type EditorOverlayProps = {
  isOpen: boolean
  onClose: () => void
  className?: string
  panelRef?: Ref<HTMLDivElement>
  children: ReactNode
}

const EditorOverlay = ({
  isOpen,
  onClose,
  className = "",
  panelRef,
  children,
}: EditorOverlayProps) => {
  const portalRoot = useEditorPortalRoot()

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation()
        onClose()
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [isOpen, onClose])

  const handleBackdropMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    if (target.closest(".ML__keyboard")) return
    if (event.target !== event.currentTarget) return
    onClose()
  }

  if (!isOpen) return null

  return createPortal(
    <div
      className='tiptap-editor__overlay-backdrop'
      onMouseDown={handleBackdropMouseDown}
      role='presentation'
    >
      <div
        ref={panelRef}
        className={`tiptap-editor__overlay-panel ${className}`.trim()}
        onMouseDown={(event) => event.stopPropagation()}
        role='dialog'
        aria-modal='true'
      >
        {children}
      </div>
    </div>,
    portalRoot ?? document.body
  )
}

export default EditorOverlay
